import React, { useEffect, useState } from "react";
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchVendors,
  registerVendor,
  updateVendorAction,
  updateVendorApprovalAction,
} from "store/features/vendor/vendorActions";
import {
  selectVendors,
  selectVendorsLoading,
  selectVendorsError,
} from "store/features/vendor/vendorSelectors";
import Modal from "components/modal/index";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

const Vendors = () => {
  const dispatch = useDispatch();
  const vendors = useSelector(selectVendors);
  const loading = useSelector(selectVendorsLoading);
  const error = useSelector(selectVendorsError);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentVendor, setCurrentVendor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    businessName: "",
    contactEmail: "",
    contactPhone: "",
    password:""
  });

  useEffect(() => {
    dispatch(fetchVendors());
  }, [dispatch]);

  // Filter vendors based on search term and status
  const filteredVendors = vendors.filter((vendor) => {
    const matchesSearch = Object.values(vendor).some(
      (value) =>
        value &&
        typeof value === "string" &&
        value.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesStatus =
      statusFilter === "All" ||
      (statusFilter === "Approved" && vendor.isApproved) ||
      (statusFilter === "Pending" && !vendor.isApproved);
    return matchesSearch && matchesStatus;
  });

  const handleAddVendor = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      businessName: "",
      contactEmail: "",
      contactPhone: "",
    });
    setIsAddModalOpen(true);
  };

  const handleEdit = (vendor) => {
    setCurrentVendor(vendor);
    setFormData({
      name: vendor.name,
      email: vendor.email,
      phone: vendor.phone,
      businessName: vendor.businessName,
      contactEmail: vendor.contactEmail,
      contactPhone: vendor.contactPhone,
    });
    setIsEditModalOpen(true);
  };

  const handleStatusChange = (vendor, isApproved) => {
    dispatch(updateVendorApprovalAction(vendor._id, isApproved));
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  const handleAddSubmit = (e) => {
    e.preventDefault();
    dispatch(registerVendor(formData))
      .then(() => {
        showSuccessToast("Vendor created successfully");
        setIsAddModalOpen(false);
         dispatch(fetchVendors());
      })
      .catch((error) => {
        showErrorToast(error.message || "Failed to create vendor");
      });
  };


  const handleEditSubmit = (e) => {
    e.preventDefault();
    dispatch(updateVendorAction(currentVendor._id, formData))
      .then(() => {
        showSuccessToast("Vendor updated successfully");
        setIsEditModalOpen(false);
      })
      .catch((error) => {
        showErrorToast(error.message || "Failed to update vendor");
      });
  };

  
  if (loading)
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;

  return (
    <div className="p-2 h-screen ">
      {/* Header Section */}
      <div className="mb-8 flex flex-col items-start justify-between md:flex-row md:items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Vendors Management</h1>
          <p className="text-slate-600">Manage all your vendors in one place</p>
        </div>

        <div className="mt-4 flex w-full flex-col space-y-3 sm:flex-row sm:items-center sm:space-x-4 sm:space-y-0 md:mt-0 md:w-auto">
          <div className="relative flex-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <FiSearch className="text-slate-400" />
            </div>
            <input
              type="text"
              placeholder="Search vendors..."
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-4 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          >
            <option value="All">All Statuses</option>
            <option value="Approved">Approved</option>
            <option value="Pending">Pending</option>
          </select>
          <button
            onClick={handleAddVendor}
            className="flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <FiPlus className="mr-2 h-5 w-5" />
            Add Vendor
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mb-6 grid grid-cols-1 gap-5 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-medium text-slate-600">Total Vendors</h3>
          <p className="mt-2 text-3xl font-bold text-slate-800">{vendors.length}</p>
          <div className="mt-2 h-1 w-full bg-slate-100">
            <div className="h-1 w-full bg-blue-500"></div>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-medium text-slate-600">Approved Vendors</h3>
          <p className="mt-2 text-3xl font-bold text-green-600">
            {vendors.filter((v) => v.isApproved).length}
          </p>
          <div className="mt-2 h-1 w-full bg-slate-100">
            <div className="h-1 w-full bg-green-500"></div>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-medium text-slate-600">Pending Approval</h3>
          <p className="mt-2 text-3xl font-bold text-amber-500">
            {vendors.filter((v) => !v.isApproved).length}
          </p>
          <div className="mt-2 h-1 w-full bg-slate-100">
            <div className="h-1 w-full bg-amber-400"></div>
          </div>
        </div>
      </div>

      {/* Vendors Table */}
      <div className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-indigo-600 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider ">
                  Vendor Name
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider ">
                  Business Owner
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider ">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider ">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider ">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider ">
                  Joined Date
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider ">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {filteredVendors.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <FiSearch className="h-12 w-12 text-slate-400" />
                      <p className="mt-2 text-lg font-medium text-slate-700">
                        {searchTerm
                          ? "No vendors match your search"
                          : "No vendors found"}
                      </p>
                      <button
                        onClick={handleAddVendor}
                        className="mt-4 text-blue-600 hover:text-blue-800"
                      >
                        Add your first vendor
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredVendors.map((vendor) => (
                  <tr key={vendor._id} className="hover:bg-slate-50">
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm font-medium text-slate-800">
                        {vendor.businessName}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-slate-700">{vendor.name}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-slate-700">{vendor.email}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-slate-700">{vendor.phone}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                          vendor.isApproved
                            ? "bg-green-100 text-green-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {vendor.isApproved ? "Approved" : "Pending"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-slate-700">
                        {new Date(vendor.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleEdit(vendor)}
                          className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                          title="Edit"
                        >
                          <FiEdit2 className="h-5 w-5" />
                        </button>
                        {!vendor.isApproved ? (
                          <button
                            onClick={() => handleStatusChange(vendor, true)}
                            className="rounded-lg bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700"
                          >
                            Approve
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStatusChange(vendor, false)}
                            className="rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Vendor Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
        <div className="p-6">
          <h2 className="mb-6 text-2xl font-bold text-slate-800">Add New Vendor</h2>
          <form onSubmit={handleAddSubmit}>
            <div className="mb-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
               Owner Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  required
                />
              </div>
              
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Business Email
                </label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Business Phone
                </label>
                <input
                  type="text"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                Create Vendor
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Edit Vendor Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <div className="p-6">
          <h2 className="mb-6 text-2xl font-bold text-slate-800">
            Edit Vendor: {currentVendor?.businessName}
          </h2>
          <form onSubmit={handleEditSubmit}>
            <div className="mb-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                 Business Owner <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Business Email
                </label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Business Phone
                </label>
                <input
                  type="text"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border border-slate-300 bg-white p-2.5 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                Update Vendor
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default Vendors;